import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { focusContainerOnTouch } from '@/components/shared/touchInput';
import { formatPatientNameDisplay } from '@/components/studies/worklistDisplay';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { MacroPopover } from '@/components/viewer/report/MacroPopover';
import { useAiAvailability } from '@/hooks/use-ai-availability';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useIsTcMember } from '@/hooks/use-is-tc-member';
import { useMacroExpansion } from '@/hooks/use-macro-expansion';
import { useReportMacros } from '@/hooks/use-report-macros';
import { useSettings } from '@/hooks/use-settings';
import { useToast } from '@/hooks/use-toast';
import { askClaude, askClaudeWithImages } from '@/lib/ai/claude';
import { collectSeriesThumbnails } from '@/lib/ai/thumbnailToBase64';
import { createBundle, getAvailableGroups, getBundleCandidatesForStudy, getConsultationForStudy, getGroupRequestTemplates, getStudy, submitConsultation, submitConsultationAmendment, } from '@/lib/api';
import { findBestTemplate } from '@/lib/data/reportTemplateMatching';
import { buildRequestAnswers, CLINICAL_HISTORY_KEY, effectiveRequestFields, isBlankRequestValue, localizeRequestFieldErrors, preserveRequestValues, REQUEST_SELECT_NONE, requestFieldInputId, SPECIFIC_QUESTIONS_KEY, splitRequestValues, validateRequestValues, } from '@/lib/data/requestFields';
import { formatDate, formatDateTime, formatDecimal, formatNumber, NUMERIC_DATE, NUMERIC_DATE_TIME, } from '@/lib/format';
import { formatPatientAge } from '@/lib/patientAge';
import { queryClient } from '@/lib/queryClient';
import { ALL_BREEDS, hasBreedList, loadSpeciesBreeds, resolveSpeciesKey, searchBreeds, SPECIES, SPECIES_WITHOUT_BREEDS, } from '@/lib/speciesBreeds';
import { cn } from '@/lib/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronDown, Layers, Loader2, Send, ShieldCheck, } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BundleStudyPicker } from './BundleStudyPicker';
import { EmailChipsInput } from './EmailChipsInput';
import { RequestFieldsForm } from './RequestFieldsForm';
import { SubmitSeriesSelector } from './SubmitSeriesSelector';
import { formatWeight, parseWeight } from './weightFormat';
// Dot color per fixed priority tier.
const PRIORITY_DOT_CLASS = {
    stat: 'bg-red-500',
    urgent: 'bg-amber-500',
    routine: 'bg-emerald-500',
};
// Fallback priorities shown when no specific group is resolved (auto-route, or
// multiple connected groups). label=null so the fixed tier name localizes by key;
// the backend validates against the assigned group.
const DEFAULT_OFFERED_PRIORITIES = [
    { key: 'stat', label: null, tatMinutes: 60 },
    { key: 'urgent', label: null, tatMinutes: 240 },
    { key: 'routine', label: null, tatMinutes: 1440 },
];
// i18n keys for the fixed tier display names (reused from the worklist toolbar).
const TIER_LABEL_KEY = {
    stat: 'toolbar.priorityStat',
    urgent: 'toolbar.priorityUrgent',
    routine: 'toolbar.priorityRoutine',
};
/** Localized turnaround label, e.g. 30 -> "30 min", 240 -> "4 hr". */
function formatTat(minutes, minUnit, hrUnit) {
    if (minutes < 60)
        return `${minutes} ${minUnit}`;
    const hours = minutes / 60;
    return Number.isInteger(hours)
        ? `${formatNumber(hours)} ${hrUnit}`
        : `${formatDecimal(hours, 1)} ${hrUnit}`;
}
/**
 * The consultation type to pre-select: the best (modality, species, body area)
 * match under the same scoring the TC editor uses for report templates, else
 * the first template (already name ASC from the API). Returns null when the
 * group publishes none.
 */
/** An existing case's narratives as answers to the standard questions (amendment mode). */
function existingCaseAnswers(existingCase) {
    if (!existingCase)
        return {};
    return {
        [CLINICAL_HISTORY_KEY]: existingCase.clinicalHistory ?? '',
        [SPECIFIC_QUESTIONS_KEY]: existingCase.specificQuestions ?? '',
    };
}
function pickDefaultRequestTemplate(templates, context) {
    if (templates.length === 0)
        return null;
    const scored = templates.map((template) => ({
        id: template.id,
        name: template.name,
        ownerType: 'group',
        templateType: 'teleconsultation',
        modalities: template.modalities,
        bodyAreas: template.bodyAreas,
        species: template.species,
        sections: [],
        macros: [],
    }));
    const best = findBestTemplate(scored, {
        species: context.species,
        modality: context.modality,
        bodyArea: context.bodyArea,
        templateType: 'teleconsultation',
    });
    return (best && templates.find((template) => template.id === best.id)) ?? templates[0];
}
// ═══════════════════════════════════════════
// AI Polish — cleans up free-text input via Claude
// ═══════════════════════════════════════════
async function aiPolishText(text, field, studyId) {
    const trimmed = text.trim();
    if (!trimmed)
        return trimmed;
    if (field === 'history') {
        return askClaude('You are an editor that polishes veterinary clinical history text for radiology consultation requests. Fix grammar, spelling, and punctuation. Expand common abbreviations (dx, hx, sx, tx, rx, pt, w/o, b/l). Organize into clear sentences. Keep the medical content exactly the same — do not add information. Return only the polished text, nothing else.', trimmed, { maxTokens: 512, studyId });
    }
    return askClaude('You are an editor that polishes questions written by veterinary clinicians for radiologist consultations. Fix grammar and punctuation. If there are multiple questions, number them. Ensure each ends with a question mark. Keep the medical content exactly the same — do not add information. Return only the polished text, nothing else.', trimmed, { maxTokens: 512, studyId });
}
export function SubmitConsultationDialog({ open, onClose, study, existingCase = null, origin, }) {
    const { toast } = useToast();
    const { t } = useTranslation('teleconsultation');
    const { currentUser, isSuperAdmin } = useCurrentUser();
    const { canAccessTeleconsultation } = useIsTcMember();
    const { openSettings } = useSettings();
    // AI Review (polish + vision review) needs the region-wide switch AND this
    // study's clinic to have opted in (ai_assist); otherwise the backend refuses
    // /ai/complete, so the affordance is hidden entirely. Submit itself never
    // calls into the model, so it keeps working unaffected.
    const { availability: aiAvailability } = useAiAvailability(study.id);
    const aiAgentEnabled = aiAvailability?.available === true;
    const isAmendment = !!existingCase;
    // The submitting clinic is always the study's own clinic — that's what the
    // backend uses to validate membership, resolve group connections, and stamp
    // submitting_clinic_id (it never reads a clinicId from the request body).
    // Deriving it from the user's clinicRoles broke submission for super admins
    // (empty clinicRoles → null → Submit stuck disabled) and could resolve to the
    // wrong clinic for multi-clinic users.
    const clinicId = study.clinicId;
    // Authoritative: the backend reports how many images still owe us pixels, so
    // this needs no extra fetch and no inference from count deltas.
    const stillReceiving = (study.counts?.pendingImageCount ?? 0) > 0;
    // Whether the user can fix a "no connected group" situation themselves from
    // Settings → Teleradiology Groups (connect a clinic, or create a group that
    // auto-connects it). Clinic admins and super admins can; others are told to
    // ask one.
    const canManageGroups = isSuperAdmin || (currentUser?.clinicRoles.some((cr) => cr.roleName === 'Clinic Admin') ?? false);
    // Fetch full study details for series list + real thumbnails
    const { data: studyDetails, isLoading: isLoadingStudy } = useQuery({
        queryKey: ['study-detail', study.id],
        queryFn: () => getStudy(study.id),
        enabled: open,
        staleTime: 5 * 60_000,
    });
    // Fetch groups connected to this study's clinic (for target group selector)
    const { data: groups = [], isLoading: groupsLoading } = useQuery({
        queryKey: ['available-groups', study.clinicId],
        queryFn: () => getAvailableGroups(study.clinicId),
        staleTime: 5 * 60_000,
    });
    // Related TC cases for the same patient — for optional visit bundling (3.10).
    // GET bundle-candidates requires the caller to be a teleconsultation group
    // member (RequireGroupMemberAsync) or super admin — a plain submitting-clinic
    // user (the common case) is neither, so skip the call entirely rather than
    // firing a request that is guaranteed to 403. canAccessTeleconsultation is
    // the same signal the TC worklist and nav gate on.
    const { data: relatedCandidates = [] } = useQuery({
        queryKey: ['tc-bundle-candidates-study', study.id],
        queryFn: () => getBundleCandidatesForStudy(study.id),
        enabled: open && !existingCase && canAccessTeleconsultation,
        staleTime: 60_000,
    });
    const [relatedCaseIds, setRelatedCaseIds] = useState([]);
    // Series from real study data
    const seriesList = useMemo(() => studyDetails?.series ?? [], [studyDetails?.series]);
    // ── Patient info state (pre-filled from study / DICOM tags) ──
    const [patientName, setPatientName] = useState(study.patient.name ?? '');
    const [ownerName, setOwnerName] = useState(study.patient.ownerName ?? '');
    const [species, setSpecies] = useState(study.patient.species ?? '');
    const [breed, setBreed] = useState('');
    const [age, setAge] = useState(() => formatPatientAge(study.patient.age, t));
    const [weightValue, setWeightValue] = useState(() => parseWeight(study.patient.weight).value);
    const [weightUnit, setWeightUnit] = useState(() => parseWeight(study.patient.weight).unit);
    const [sex, setSex] = useState(study.patient.sex ?? '');
    // ── Series selection (all checked by default) ──
    const [selectedSeriesUids, setSelectedSeriesUids] = useState(new Set());
    // ── Consultation details ──
    const [priority, setPriority] = useState('routine');
    // Every answer on the request form, the standard questions included, keyed by
    // field key. An amendment shows the existing case's narratives read-only.
    const [requestValues, setRequestValues] = useState(() => existingCaseAnswers(existingCase));
    const [targetGroupId, setTargetGroupId] = useState(null);
    const [targetRadiologistId, setTargetRadiologistId] = useState(null);
    const [internalNotes, setInternalNotes] = useState('');
    // Update selected series when study details load. Guarded so refetches with the same series
    // reference don't clobber a user's manual deselection.
    const prevSeriesRef = useRef(undefined);
    useEffect(() => {
        const series = studyDetails?.series;
        if (series && series !== prevSeriesRef.current) {
            prevSeriesRef.current = series;
            setSelectedSeriesUids(new Set(series.map((s) => s.seriesUid)));
        }
    }, [studyDetails?.series]);
    // Re-initialize amendment fields when dialog opens with an existing case.
    // Uses the React-docs "adjusting state during render" pattern.
    const [prevOpen, setPrevOpen] = useState(open);
    const [prevExistingCase, setPrevExistingCase] = useState(existingCase);
    if (open !== prevOpen || existingCase !== prevExistingCase) {
        setPrevOpen(open);
        setPrevExistingCase(existingCase);
        if (open && existingCase) {
            setRequestValues(existingCaseAnswers(existingCase));
        }
    }
    // ── Request form (consultation type + its structured intake questions) ──
    // A request template belongs to exactly one group, so everything here is
    // scoped to the resolved group: the explicitly targeted one, or the sole
    // connected one. Auto-route across several groups resolves to null and the
    // picker degrades to an explanatory hint.
    const resolvedRequestGroupId = targetGroupId ?? (groups.length === 1 ? groups[0].id : null);
    const { data: requestTemplates = [], isLoading: requestTemplatesLoading } = useQuery({
        queryKey: ['tc-request-templates', resolvedRequestGroupId],
        queryFn: () => getGroupRequestTemplates(resolvedRequestGroupId),
        enabled: open && !isAmendment && !!resolvedRequestGroupId,
        staleTime: 5 * 60_000,
    });
    const [requestTemplateId, setRequestTemplateId] = useState(null);
    const [requestErrors, setRequestErrors] = useState({});
    // True once the submitter picks a consultation type themselves — the default
    // resolution must never overwrite an explicit choice (including "None").
    const [requestTemplateTouched, setRequestTemplateTouched] = useState(false);
    // Switching the target group invalidates the whole request form: its
    // templates belong to the previous group. Uses the React-docs "adjusting
    // state during render" pattern (no effect, no intermediate paint).
    const [prevRequestGroupId, setPrevRequestGroupId] = useState(resolvedRequestGroupId);
    // Seed the picker with the resolved default once the group's templates land.
    // Re-arming on group change is what makes the reset above re-seed for the new
    // group (React Query hands back [] the moment the key changes, so the seed
    // never runs against the previous group's templates).
    const requestTemplateIds = requestTemplates.map((template) => template.id).join(',');
    const [seededRequestTemplateIds, setSeededRequestTemplateIds] = useState(null);
    if (resolvedRequestGroupId !== prevRequestGroupId) {
        setPrevRequestGroupId(resolvedRequestGroupId);
        setRequestTemplateId(null);
        setRequestValues({});
        setRequestErrors({});
        setRequestTemplateTouched(false);
        setSeededRequestTemplateIds(null);
    }
    if (!isAmendment &&
        !requestTemplateTouched &&
        requestTemplates.length > 0 &&
        requestTemplateIds !== seededRequestTemplateIds) {
        setSeededRequestTemplateIds(requestTemplateIds);
        if (requestTemplateId === null) {
            setRequestTemplateId(pickDefaultRequestTemplate(requestTemplates, {
                species: species || study.patient.species,
                modality: study.study.modality,
                bodyArea: study.study.description,
            })?.id ?? null);
        }
    }
    const selectedRequestTemplate = requestTemplates.find((template) => template.id === requestTemplateId) ?? null;
    // The form the submitter answers: the picked type's own questions, or the
    // standard ones (Clinical History, Specific Questions) when it defines none.
    // Nothing about the form is fixed in this dialog.
    const localize = useCallback((key) => String(t(key)), [t]);
    const requestFields = useMemo(() => effectiveRequestFields(selectedRequestTemplate?.requestFields, localize), [selectedRequestTemplate, localize]);
    const handleRequestTemplateChange = useCallback((nextId) => {
        setRequestTemplateTouched(true);
        setRequestErrors({});
        const formFor = (id) => effectiveRequestFields(requestTemplates.find((template) => template.id === id)?.requestFields, localize);
        setRequestValues((prev) => preserveRequestValues(prev, formFor(requestTemplateId), formFor(nextId)));
        setRequestTemplateId(nextId);
    }, [requestTemplates, requestTemplateId, localize]);
    const handleRequestValueChange = useCallback((key, value) => {
        setRequestValues((prev) => ({ ...prev, [key]: value }));
        setRequestErrors((prev) => {
            if (!(key in prev))
                return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);
    // ── Email notifications ──
    const [notifyEmails, setNotifyEmails] = useState([]);
    // True once the user has added/removed a chip themselves — inherited
    // connection defaults must never clobber a manual edit (including "remove
    // them all", which the backend then re-inherits at submit).
    const [emailsEdited, setEmailsEdited] = useState(false);
    const handleNotifyEmailsChange = useCallback((emails) => {
        setEmailsEdited(true);
        setNotifyEmails(emails);
    }, []);
    // The connection's default notification emails for the resolved group (the
    // explicitly targeted group, or the sole connected one). Mirrors
    // ConsultationDetailsSection's group resolution.
    const inheritedEmails = useMemo(() => {
        const group = targetGroupId
            ? (groups.find((g) => g.id === targetGroupId) ?? null)
            : groups.length === 1
                ? groups[0]
                : null;
        return group?.defaultNotificationEmails ?? [];
    }, [groups, targetGroupId]);
    // Pre-fill the chips with the inherited defaults so the submitter sees who
    // will be notified. Only while the list is untouched — switching the target
    // group re-seeds until the user takes over. Uses the React-docs "adjusting
    // state during render" pattern (no effect).
    const inheritedKey = inheritedEmails.join(',');
    const [prevInheritedKey, setPrevInheritedKey] = useState(null);
    if (open && !isAmendment && !emailsEdited && inheritedKey !== prevInheritedKey) {
        setPrevInheritedKey(inheritedKey);
        setNotifyEmails(inheritedEmails);
    }
    // ── Mutations ──
    const submitMutation = useMutation({
        mutationFn: submitConsultation,
        onSuccess: async (created) => {
            // Optional visit bundling: if the user added related studies, bundle the
            // freshly-created case with them. Best-effort — failures don't block the
            // successful submission.
            if (relatedCaseIds.length > 0) {
                try {
                    const created = await getConsultationForStudy(study.id);
                    if (created?.id) {
                        await createBundle({ caseIds: [created.id, ...relatedCaseIds] });
                    }
                }
                catch (err) {
                    console.error('Bundling related studies failed:', err);
                }
            }
            // Link-shell submissions feed the dev origin bridge (no-op in prod)
            // so the TC worklist can badge the case before the backend stamps
            // submitterTier. The POST response carries the created id; the
            // by-study lookup is only a fallback. Best-effort like bundling.
            if (origin === 'link') {
                try {
                    const caseId = created?.id ?? (await getConsultationForStudy(study.id))?.id;
                    if (caseId) {
                        const { recordLinkOriginCase } = await import('@/lib/api/link-dev-origin');
                        recordLinkOriginCase(caseId);
                    }
                }
                catch (err) {
                    console.error('Recording Link origin failed:', err);
                }
            }
            queryClient.invalidateQueries({ queryKey: ['teleconsultation-worklist'] });
            toast({
                title: t('submit.toastSubmitted'),
                description: t('submit.toastSubmittedDesc', {
                    name: study.patient.name ?? t('row.study'),
                    priority,
                }),
            });
            handleClose();
        },
        onError: (error) => {
            toast({
                title: t('submit.toastFailed'),
                description: error.message || t('submit.toastFailedDesc'),
                variant: 'destructive',
            });
        },
    });
    const amendMutation = useMutation({
        mutationFn: submitConsultationAmendment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['teleconsultation-worklist'] });
            toast({
                title: t('submit.toastAmendmentSubmitted'),
                description: t('submit.toastAmendmentDesc'),
            });
            handleClose();
        },
        onError: (error) => {
            toast({
                title: t('submit.toastFailed'),
                description: error.message || t('submit.toastFailedDesc'),
                variant: 'destructive',
            });
        },
    });
    // ── AI Review (polish + mismatch + positioning check) ──
    const [isReviewing, setIsReviewing] = useState(false);
    const [reviewResult, setReviewResult] = useState(null);
    const [showReviewDialog, setShowReviewDialog] = useState(false);
    const handleAIReview = useCallback(async () => {
        if (isReviewing)
            return;
        setIsReviewing(true);
        setReviewResult(null);
        try {
            // Step 1: Polish every long-text answer on the form, in parallel with
            // thumbnail collection. The standard "questions for the radiologist"
            // keeps its own prompt; every other narrative is polished as history.
            const reviewAnswers = buildRequestAnswers(requestFields, requestValues, {
                yes: localize('submit.fieldYes'),
                no: localize('submit.fieldNo'),
            });
            const polishTasks = [];
            for (const field of requestFields) {
                if (field.type !== 'textarea')
                    continue;
                const answer = requestValues[field.key];
                if (typeof answer !== 'string' || answer.trim().length < 3)
                    continue;
                polishTasks.push(aiPolishText(answer, field.key === SPECIFIC_QUESTIONS_KEY ? 'questions' : 'history', study.id).then((polished) => setRequestValues((prev) => ({ ...prev, [field.key]: polished }))));
            }
            // Step 2: Collect thumbnails (1 per series, max 8)
            const thumbnailTask = seriesList.length > 0
                ? collectSeriesThumbnails(seriesList, selectedSeriesUids)
                : Promise.resolve([]);
            const [, thumbnails] = await Promise.all([Promise.all(polishTasks), thumbnailTask]);
            // Step 3: Build multimodal prompt for Claude Vision
            const content = [];
            content.push({
                type: 'text',
                text: [
                    'Review this veterinary radiology consultation request for potential issues.',
                    '',
                    'PATIENT INFO (entered on form):',
                    `- Name: ${patientName || '(empty)'}`,
                    `- Owner: ${ownerName || '(empty)'}`,
                    `- Species: ${species || '(empty)'}`,
                    `- Sex: ${sex || '(empty)'}`,
                    `- Age: ${age || '(empty)'}`,
                    `- Weight: ${formatWeight(weightValue, weightUnit) || '(empty)'}`,
                    '',
                    'DICOM HEADER INFO (from the study file):',
                    `- Patient Name: ${study.patient.name || '(empty)'}`,
                    `- Species: ${study.patient.species || '(empty)'}`,
                    `- Sex: ${study.patient.sex || '(empty)'}`,
                    `- Age: ${formatPatientAge(study.patient.age, t) || '(empty)'}`,
                    `- Study Description: ${study.study.description || '(empty)'}`,
                    `- Modality: ${study.study.modality || '(empty)'}`,
                    `- Study Date: ${study.study.dateTime ? new Date(study.study.dateTime).toLocaleDateString() : '(empty)'}`,
                    `- Accession: ${study.study.accessionNumber || '(empty)'}`,
                    '',
                    'CONSULTATION REQUEST (as entered on the form):',
                    ...(reviewAnswers.length > 0
                        ? reviewAnswers.map((answer) => `- ${answer.label}: ${answer.value}`)
                        : ['- (empty)']),
                    '',
                    thumbnails.length > 0
                        ? `ATTACHED: ${thumbnails.length} series thumbnail image(s) — one representative image per series.`
                        : 'NO IMAGES AVAILABLE for visual review.',
                    '',
                    'Check for:',
                    '1. Patient info mismatches between form fields and DICOM headers that might indicate the wrong case was selected (e.g. different patient name, species, sex)',
                    '2. Obvious positioning or image quality problems visible in the thumbnails',
                    '3. Whether the images appear to match the clinical question being asked (e.g. asking about a limb but thorax images attached)',
                    '4. Any other red flags a technician should know about before submitting',
                    '',
                    'Return ONLY valid JSON (no markdown, no code fences) in this exact format:',
                    '{"issues":[{"severity":"warning|error","title":"Short title","detail":"Explanation"}],"summary":"One-sentence summary"}',
                    'If everything looks good, return: {"issues":[],"summary":"No issues found. The consultation request looks complete and consistent."}',
                ].join('\n'),
            });
            for (const thumb of thumbnails) {
                content.push({
                    type: 'image',
                    source: { type: 'base64', media_type: thumb.mediaType, data: thumb.base64 },
                });
                content.push({ type: 'text', text: `(Series: ${thumb.label})` });
            }
            const raw = await askClaudeWithImages('You are a veterinary radiology QA assistant. You review consultation requests before they are sent to radiologists. Be concise and specific. Only flag genuine issues — do not flag minor formatting differences in names (e.g. "LAST^FIRST" vs "Last, First" is normal DICOM encoding). Return valid JSON only.', content, { maxTokens: 1024, studyId: study.id });
            // Parse the JSON response (strip any markdown fences Claude might add)
            const cleaned = raw
                .replace(/^```json?\s*/i, '')
                .replace(/```\s*$/, '')
                .trim();
            const parsed = JSON.parse(cleaned);
            setReviewResult(parsed);
            setShowReviewDialog(true);
        }
        catch (err) {
            console.error('AI Review failed:', err);
            setReviewResult({
                issues: [
                    {
                        severity: 'warning',
                        title: t('submit.reviewUnavailableTitle'),
                        detail: t('submit.reviewUnavailableDetail'),
                    },
                ],
                summary: t('submit.reviewUnavailableSummary'),
            });
            setShowReviewDialog(true);
        }
        finally {
            setIsReviewing(false);
        }
    }, [
        isReviewing,
        requestFields,
        requestValues,
        localize,
        seriesList,
        selectedSeriesUids,
        patientName,
        ownerName,
        species,
        sex,
        age,
        weightValue,
        weightUnit,
        study,
        t,
        setShowReviewDialog,
    ]);
    function handleClose() {
        onClose();
        setInternalNotes('');
        setNotifyEmails([]);
        setEmailsEdited(false);
        setPrevInheritedKey(null);
        setPriority('routine');
        setTargetGroupId(null);
        setRelatedCaseIds([]);
        setRequestTemplateId(null);
        setRequestValues({});
        setRequestErrors({});
        setRequestTemplateTouched(false);
        setSeededRequestTemplateIds(null);
    }
    function handleSubmit() {
        // Every question on the form, the standard ones included, blocks submission
        // the same way the backend would 400: required answers present, values in
        // range / in options.
        if (!isAmendment) {
            const violations = validateRequestValues(requestFields, requestValues);
            if (violations.length > 0) {
                const messages = localizeRequestFieldErrors(violations, (key, params) => String(t(key, params ?? {})));
                setRequestErrors(messages);
                toast({
                    title: t('submit.toastRequestFieldsTitle'),
                    description: t('submit.toastRequestFieldsDesc'),
                    variant: 'destructive',
                });
                const firstInvalid = document.getElementById(requestFieldInputId(violations[0].key));
                firstInvalid?.scrollIntoView({ block: 'center' });
                firstInvalid?.focus();
                return;
            }
        }
        if (isAmendment) {
            amendMutation.mutate({
                caseId: existingCase.id,
                clinicalHistory: '',
                specificQuestions: undefined,
                notifyEmails: notifyEmails.length > 0 ? notifyEmails : undefined,
                internalNotes: internalNotes.trim() || undefined,
            });
        }
        else {
            if (!clinicId)
                return;
            // Series scoping: if the user selected every series, send undefined so
            // the case has no include-list (NULL in DB = "show everything"). If
            // they selected a strict subset, send the series DB ids. Zero selected
            // is nonsense and is blocked below.
            if (seriesList.length > 0 && selectedSeriesUids.size === 0) {
                toast({
                    title: t('submit.toastNoSeriesTitle'),
                    description: t('submit.toastNoSeriesDesc'),
                    variant: 'destructive',
                });
                return;
            }
            const includedSeriesIds = seriesList.length > 0 && selectedSeriesUids.size < seriesList.length
                ? seriesList.flatMap((s) => (selectedSeriesUids.has(s.seriesUid) ? [s.id] : []))
                : undefined;
            // The reserved standard questions travel as the case's own members; every
            // other answer goes in requestValues.
            const answers = splitRequestValues(requestFields, requestValues, {
                yes: localize('submit.fieldYes'),
                no: localize('submit.fieldNo'),
            });
            submitMutation.mutate({
                studyId: study.id,
                clinicId,
                clinicalHistory: answers.clinicalHistory,
                specificQuestions: answers.specificQuestions || undefined,
                priority,
                targetGroupId: targetGroupId || undefined,
                targetRadiologistId: targetRadiologistId || undefined,
                notifyEmails: notifyEmails.length > 0 ? notifyEmails : undefined,
                internalNotes: internalNotes.trim() || undefined,
                includedSeriesIds,
                patient: {
                    id: study.patient.id || null,
                    name: patientName || study.patient.name,
                    species: species || study.patient.species,
                    ownerName: ownerName || study.patient.ownerName,
                    breed: breed || null,
                    age: age || null,
                    weight: formatWeight(weightValue, weightUnit),
                    sex: sex || null,
                },
                study: {
                    description: study.study.description,
                    modality: study.study.modality,
                    dateTime: study.study.dateTime,
                    accessionNumber: study.study.accessionNumber,
                },
                // A case that shares only some series reports the counts of those series: the
                // consultant sees these numbers, and whole-study counts would reveal what was held back.
                counts: includedSeriesIds
                    ? {
                        seriesCount: includedSeriesIds.length,
                        imageCount: seriesList
                            .filter((s) => selectedSeriesUids.has(s.seriesUid))
                            .reduce((sum, s) => sum + s.images.length, 0),
                    }
                    : {
                        seriesCount: study.counts.seriesCount,
                        imageCount: study.counts.imageCount,
                    },
                // Both or neither: the backend 400s on values without a template.
                requestTemplateId: selectedRequestTemplate?.id,
                requestValues: selectedRequestTemplate ? answers.requestValues : undefined,
            });
        }
    }
    const isPending = submitMutation.isPending || amendMutation.isPending;
    // AI Review needs something to review: any answer on the form with substance.
    const hasReviewableAnswer = requestFields.some((field) => {
        const answer = requestValues[field.key];
        return typeof answer === 'string' ? answer.trim().length >= 3 : !isBlankRequestValue(answer);
    });
    // Series counts
    const totalSelectedImages = seriesList
        .filter((s) => selectedSeriesUids.has(s.seriesUid))
        .reduce((sum, s) => sum + s.images.length, 0);
    const totalImages = seriesList.reduce((sum, s) => sum + s.images.length, 0);
    return (_jsx(Dialog, { open: open, onOpenChange: (v) => !v && handleClose(), children: _jsxs(DialogContent, { className: "flex max-h-[90dvh] flex-col gap-0 p-0 sm:max-w-4xl [@media(max-height:500px)]:max-h-dvh", onOpenAutoFocus: focusContainerOnTouch, "data-testid": "submit-consultation-dialog", children: [_jsxs("div", { className: "border-border border-b px-4 pt-4 pb-3 sm:px-6 sm:pt-6 sm:pb-4", children: [_jsx(DialogTitle, { className: "text-lg font-semibold", children: isAmendment ? t('submit.amendConsultation') : t('submit.requestConsultation') }), _jsxs("p", { className: "text-muted-foreground mt-1 text-sm [@media(max-height:500px)]:truncate", children: [formatPatientNameDisplay(study.patient.name), " \u2014", ' ', study.study.description ?? t('row.study'), " (", study.study.modality ?? '?', ")", study.study.dateTime && ` — ${formatDate(study.study.dateTime, NUMERIC_DATE)}`] }), isAmendment && existingCase && _jsx(AmendmentBanner, { existingCase: existingCase })] }), _jsx("div", { className: "flex-1 overflow-y-auto", children: _jsxs("div", { className: "divide-border grid min-h-0 grid-cols-1 divide-y md:grid-cols-[340px_1fr] md:divide-x md:divide-y-0", children: [_jsxs("div", { className: "space-y-6 p-4 sm:p-6 md:max-h-[calc(90dvh-200px)] md:overflow-y-auto", children: [_jsx(PatientInfoSection, { patientName: patientName, setPatientName: setPatientName, ownerName: ownerName, setOwnerName: setOwnerName, species: species, setSpecies: (v) => {
                                            setSpecies(v);
                                            setBreed('');
                                        }, breed: breed, setBreed: setBreed, age: age, setAge: setAge, weightValue: weightValue, setWeightValue: setWeightValue, weightUnit: weightUnit, setWeightUnit: setWeightUnit, sex: sex, setSex: setSex }), _jsx(SubmitSeriesSelector, { seriesList: seriesList, isLoading: isLoadingStudy, selectedSeriesUids: selectedSeriesUids, setSelectedSeriesUids: setSelectedSeriesUids, totalSelectedImages: totalSelectedImages, totalImages: totalImages, stillReceiving: stillReceiving })] }), _jsxs("div", { className: "space-y-5 p-4 sm:p-6 md:max-h-[calc(90dvh-200px)] md:overflow-y-auto", children: [_jsx(ConsultationDetailsSection, { priority: priority, setPriority: setPriority, targetGroupId: targetGroupId, setTargetGroupId: setTargetGroupId, targetRadiologistId: targetRadiologistId, setTargetRadiologistId: setTargetRadiologistId, internalNotes: internalNotes, setInternalNotes: setInternalNotes, notifyEmails: notifyEmails, setNotifyEmails: handleNotifyEmailsChange, groups: groups, groupsLoading: groupsLoading, requestTemplates: requestTemplates, requestTemplatesLoading: requestTemplatesLoading, hasResolvedRequestGroup: !!resolvedRequestGroupId, requestTemplateId: requestTemplateId, onRequestTemplateChange: handleRequestTemplateChange, requestFields: requestFields, requestValues: requestValues, onRequestValueChange: handleRequestValueChange, requestErrors: requestErrors, isAmendment: isAmendment, canManageGroups: canManageGroups, onConnectGroups: () => {
                                            handleClose();
                                            openSettings('tc_groups');
                                        } }), !isAmendment && relatedCandidates.length > 0 && (_jsx(RelatedStudiesSection, { candidates: relatedCandidates.map((c) => ({
                                            id: c.caseId,
                                            dateTime: c.studyDateTime,
                                            modality: c.studyModality,
                                            description: c.studyDescription,
                                            status: c.status,
                                            unavailable: !!c.bundleId,
                                        })), includedIds: relatedCaseIds, onAdd: (id) => setRelatedCaseIds((prev) => [...prev, id]), onRemove: (id) => setRelatedCaseIds((prev) => prev.filter((x) => x !== id)) }))] })] }) }), _jsxs("div", { className: "border-border flex flex-wrap items-center gap-2 border-t px-4 py-3 sm:px-6 sm:py-4", "data-testid": "submit-consultation-footer", children: [!isAmendment && aiAgentEnabled && (_jsxs(Button, { variant: "outline", size: "sm", onClick: handleAIReview, disabled: isReviewing || !hasReviewableAnswer, className: "border-[#8242DD]/30 text-[#8242DD] hover:bg-[#8242DD]/10", children: [isReviewing ? (_jsx(Loader2, { className: "mr-1.5 h-3.5 w-3.5 animate-spin" })) : (_jsx(ShieldCheck, { className: "mr-1.5 h-3.5 w-3.5" })), isReviewing ? t('submit.reviewing') : t('submit.aiReview')] })), _jsxs("div", { className: "ml-auto flex items-center gap-3", children: [_jsx(Button, { variant: "outline", onClick: handleClose, children: t('submit.cancel') }), _jsxs(Button, { onClick: handleSubmit, disabled: isPending || (!isAmendment && groups.length === 0), children: [isPending ? (_jsx(Loader2, { className: "mr-1.5 h-3.5 w-3.5 animate-spin" })) : (_jsx(Send, { className: "mr-1.5 h-3.5 w-3.5" })), isAmendment ? t('submit.submitAmendment') : t('submit.submitRequest')] })] })] }), showReviewDialog && reviewResult && (_jsx(AIReviewDialog, { result: reviewResult, onClose: () => setShowReviewDialog(false), onSubmitAnyway: () => {
                        setShowReviewDialog(false);
                        handleSubmit();
                    } }))] }) }));
}
// ═══════════════════════════════════════════
// Patient Info Section (left column)
// ═══════════════════════════════════════════
function PatientInfoSection({ patientName, setPatientName, ownerName, setOwnerName, species, setSpecies, breed, setBreed, age, setAge, weightValue, setWeightValue, weightUnit, setWeightUnit, sex, setSex, }) {
    const { t } = useTranslation('teleconsultation');
    const [breedOpen, setBreedOpen] = useState(false);
    const [breedSearch, setBreedSearch] = useState('');
    const [breedIndex, setBreedIndex] = useState(null);
    /* The 2,710-breed master is fetched, not bundled, so it can land after first paint. */
    useEffect(() => {
        let cancelled = false;
        loadSpeciesBreeds().then((index) => {
            if (!cancelled)
                setBreedIndex(index);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    /**
     * DICOM sends species however the modality spelled it ('canine', 'FELINE'), while the
     * master is keyed title case and its lookups are case-sensitive. Resolve once here so
     * a lowercase study still gets its breed list.
     */
    const speciesKey = resolveSpeciesKey(species);
    /**
     * Fall back to a plain text box rather than an empty dropdown: 'Other' has no breeds by
     * design, a species the picker does not know has none either, and a failed fetch leaves
     * an empty index. An empty dropdown reads as broken, whereas a text field reads as
     * "type it in", which is the only thing left to do.
     *
     * The first two checks run before the fetch lands, so those species never flicker from
     * a combobox to a text box mid-load.
     */
    const breedIsFreeText = speciesKey === null ||
        SPECIES_WITHOUT_BREEDS.includes(speciesKey) ||
        (breedIndex !== null && !hasBreedList(breedIndex, speciesKey));
    /* ALL_BREEDS, not the default cap: this popover has always offered the whole species. */
    const filteredBreeds = useMemo(() => (speciesKey ? searchBreeds(breedIndex, speciesKey, breedSearch, ALL_BREEDS) : []), [breedIndex, speciesKey, breedSearch]);
    return (_jsxs("div", { children: [_jsx("h3", { className: "text-muted-foreground mb-3 text-xs font-semibold tracking-wider uppercase", children: t('submit.patientInformation') }), _jsxs("div", { className: "space-y-3", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "patientName", className: "text-muted-foreground text-xs", children: t('submit.patientName') }), _jsx(Input, { id: "patientName", value: patientName, onChange: (e) => setPatientName(e.target.value), autoComplete: "off", autoCorrect: "off", spellCheck: false, className: "mt-1 h-8 text-sm" })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "ownerName", className: "text-muted-foreground text-xs", children: t('submit.owner') }), _jsx(Input, { id: "ownerName", value: ownerName, onChange: (e) => setOwnerName(e.target.value), placeholder: t('submit.ownerPlaceholder'), autoComplete: "off", autoCorrect: "off", spellCheck: false, className: "mt-1 h-8 text-sm" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-muted-foreground text-xs", children: t('submit.species') }), _jsxs(Select, { value: speciesKey ?? species ?? undefined, onValueChange: setSpecies, children: [_jsx(SelectTrigger, { className: "mt-1 h-8 text-sm", "aria-label": t('submit.species'), children: _jsx(SelectValue, { placeholder: t('submit.speciesPlaceholder') }) }), _jsx(SelectContent, { children: SPECIES.map((s) => (_jsx(SelectItem, { value: s, children: s }, s))) })] })] }), species && breedIsFreeText && (_jsxs("div", { children: [_jsx(Label, { htmlFor: "patientBreed", className: "text-muted-foreground text-xs", children: t('submit.breed') }), _jsx(Input, { id: "patientBreed", value: breed, onChange: (e) => setBreed(e.target.value), placeholder: t('submit.breedFreeText'), className: "mt-1 h-8 text-sm" })] })), species && !breedIsFreeText && (_jsxs("div", { children: [_jsx(Label, { className: "text-muted-foreground text-xs", children: t('submit.breed') }), _jsxs(Popover, { open: breedOpen, onOpenChange: setBreedOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { className: cn('border-border bg-background mt-1 flex h-8 w-full items-center justify-between rounded-md border px-3 text-sm', !breed && 'text-muted-foreground'), children: [_jsx("span", { className: "truncate", children: breed || t('submit.breedSearch') }), _jsx(ChevronDown, { className: "text-muted-foreground h-3.5 w-3.5" })] }) }), _jsxs(PopoverContent, { className: "w-[292px] max-w-[calc(100vw-1rem)] p-0", align: "start", collisionPadding: 8, onOpenAutoFocus: focusContainerOnTouch, children: [_jsx("div", { className: "border-border border-b p-2", children: _jsx(Input, { placeholder: t('submit.breedTypeToSearch'), "aria-label": t('submit.breedTypeToSearch'), value: breedSearch, onChange: (e) => setBreedSearch(e.target.value), autoCapitalize: "off", autoCorrect: "off", spellCheck: false, enterKeyHint: "search", className: "h-7 text-sm" }) }), _jsx("div", { className: "max-h-[200px] overflow-y-auto p-1", children: breedIndex === null ? (_jsx("p", { className: "text-muted-foreground py-3 text-center text-xs", children: t('submit.breedLoading') })) : filteredBreeds.length === 0 ? (_jsx("p", { className: "text-muted-foreground py-3 text-center text-xs", children: breedSearch ? t('submit.breedNoMatch') : t('submit.breedNone') })) : (filteredBreeds.map((b) => (_jsx("button", { onClick: () => {
                                                        setBreed(b);
                                                        setBreedOpen(false);
                                                        setBreedSearch('');
                                                    }, className: cn('hover:bg-accent flex w-full items-center rounded-sm px-2 py-1 text-sm transition-colors pointer-coarse:py-2', breed === b && 'bg-accent font-medium'), children: b }, b)))) })] })] })] })), _jsxs("div", { className: "grid grid-cols-2 gap-2", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "patientAge", className: "text-muted-foreground text-xs", children: t('submit.age') }), _jsx(Input, { id: "patientAge", value: age, onChange: (e) => setAge(e.target.value), placeholder: t('submit.agePlaceholder'), className: "mt-1 h-8 text-sm" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-muted-foreground text-xs", children: t('submit.sex') }), _jsxs(Select, { value: sex || undefined, onValueChange: setSex, children: [_jsx(SelectTrigger, { className: "mt-1 h-8 text-sm", children: _jsx(SelectValue, { placeholder: t('submit.sexPlaceholder') }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "M", children: t('submit.sexMale') }), _jsx(SelectItem, { value: "F", children: t('submit.sexFemale') }), _jsx(SelectItem, { value: "MN", children: t('submit.sexMaleNeutered') }), _jsx(SelectItem, { value: "FS", children: t('submit.sexFemaleSpayed') }), _jsx(SelectItem, { value: "U", children: t('submit.sexUnknown') })] })] })] })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "patientWeight", className: "text-muted-foreground text-xs", children: t('submit.weight') }), _jsxs("div", { className: "mt-1 flex gap-1.5", children: [_jsx(Input, { id: "patientWeight", value: weightValue, onChange: (e) => setWeightValue(e.target.value), placeholder: t('submit.weightPlaceholder'), inputMode: "decimal", enterKeyHint: "next", className: "h-8 min-w-0 flex-1 text-sm" }), _jsxs(Select, { value: weightUnit, onValueChange: (v) => setWeightUnit(v), children: [_jsx(SelectTrigger, { className: "h-8 w-[78px] text-sm", children: _jsx(SelectValue, {}) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "kg", children: t('submit.weightUnitKg') }), _jsx(SelectItem, { value: "lb", children: t('submit.weightUnitLb') })] })] })] })] })] })] }));
}
// ═══════════════════════════════════════════
// Consultation Details Section (right column)
// ═══════════════════════════════════════════
function ConsultationDetailsSection({ priority, setPriority, targetGroupId, setTargetGroupId, targetRadiologistId, setTargetRadiologistId, internalNotes, setInternalNotes, notifyEmails, setNotifyEmails, groups, groupsLoading, requestTemplates, requestTemplatesLoading, hasResolvedRequestGroup, requestTemplateId, onRequestTemplateChange, requestFields, requestValues, onRequestValueChange, requestErrors, isAmendment, canManageGroups, onConnectGroups, }) {
    const { t } = useTranslation('teleconsultation');
    // Macros in the free-text fields: only "All templates" macros apply, as no
    // report template is open while a case is being sent.
    const { macros } = useReportMacros();
    const { textareaRef: notesMacrosRef, handleKeyDown: notesMacrosKeyDown, insertAtCaret: notesMacrosInsert, hasMacros: notesMacrosHasMacros, picker: notesMacrosPicker, } = useMacroExpansion({
        value: internalNotes,
        onChange: setInternalNotes,
        macros,
    });
    // Resolve which group's priorities to show: the explicitly targeted group, or
    // the sole connected group, otherwise the system defaults (auto-route picks a
    // group server-side, which then validates the chosen priority).
    const selectedGroup = useMemo(() => {
        if (targetGroupId)
            return groups.find((g) => g.id === targetGroupId) ?? null;
        if (groups.length === 1)
            return groups[0];
        return null;
    }, [groups, targetGroupId]);
    const offeredPriorities = selectedGroup?.priorities && selectedGroup.priorities.length > 0
        ? selectedGroup.priorities
        : DEFAULT_OFFERED_PRIORITIES;
    const offeredKeys = offeredPriorities.map((p) => p.key).join(',');
    // Keep the selected priority valid for the resolved group's offered set.
    useEffect(() => {
        if (!offeredPriorities.some((p) => p.key === priority)) {
            const fallback = offeredPriorities.find((p) => p.key === 'routine') ?? offeredPriorities[0];
            if (fallback)
                setPriority(fallback.key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- gate on the offered set; setPriority/priority are stable enough here
    }, [offeredKeys]);
    // A specialist belongs to a specific group; if the resolved group changes (or
    // auto-route is chosen), drop a now-invalid specialist selection.
    useEffect(() => {
        if (!targetRadiologistId)
            return;
        const stillValid = selectedGroup?.radiologists?.some((r) => r.id === targetRadiologistId) ?? false;
        if (!stillValid)
            setTargetRadiologistId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- gate on the resolved group identity
    }, [selectedGroup?.id]);
    return (_jsxs("div", { className: "space-y-5", children: [!isAmendment && groups.length > 0 && (_jsxs("div", { children: [_jsxs(Label, { className: "text-muted-foreground text-xs", children: [t('submit.targetGroup'), _jsx("span", { className: "text-muted-foreground/50 ml-1", children: t('submit.optional') })] }), _jsxs(Select, { value: targetGroupId ?? 'auto', onValueChange: (v) => setTargetGroupId(v === 'auto' ? null : v), children: [_jsx(SelectTrigger, { className: "mt-1 h-8 text-sm", children: _jsx(SelectValue, { placeholder: t('submit.autoRoute') }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "auto", children: t('submit.autoRoute') }), groups.map((g) => (_jsx(SelectItem, { value: g.id, children: g.name }, g.id)))] })] })] })), !isAmendment && groups.length > 0 && (_jsxs("div", { children: [_jsx(Label, { htmlFor: "consultationType", className: "text-muted-foreground text-xs", children: t('submit.consultationType') }), !hasResolvedRequestGroup ? (_jsx("p", { className: "text-muted-foreground mt-1 text-[10px]", children: t('submit.consultationTypeAutoHint') })) : requestTemplatesLoading ? (_jsx("p", { className: "text-muted-foreground mt-1 text-[10px]", children: t('submit.consultationTypeLoading') })) : requestTemplates.length === 0 ? (_jsx("p", { className: "text-muted-foreground mt-1 text-[10px]", children: t('submit.consultationTypeEmptyHint') })) : (_jsxs(Select, { value: requestTemplateId ?? REQUEST_SELECT_NONE, onValueChange: (v) => onRequestTemplateChange(v === REQUEST_SELECT_NONE ? null : v), children: [_jsx(SelectTrigger, { id: "consultationType", className: "mt-1 h-8 text-sm", children: _jsx(SelectValue, {}) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: REQUEST_SELECT_NONE, children: t('submit.consultationTypeNone') }), requestTemplates.map((template) => (_jsx(SelectItem, { value: template.id, children: template.name }, template.id)))] })] }))] })), !isAmendment && selectedGroup && (selectedGroup.radiologists?.length ?? 0) > 0 && (_jsxs("div", { children: [_jsxs(Label, { className: "text-muted-foreground text-xs", children: [t('submit.specialistLabel'), _jsx("span", { className: "text-muted-foreground/50 ml-1", children: t('submit.optional') })] }), _jsxs(Select, { value: targetRadiologistId ?? 'any', onValueChange: (v) => setTargetRadiologistId(v === 'any' ? null : v), children: [_jsx(SelectTrigger, { className: "mt-1 h-8 text-sm", children: _jsx(SelectValue, { placeholder: t('submit.anyRadiologist') }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "any", children: t('submit.anyRadiologist') }), (selectedGroup.radiologists ?? []).map((r) => (_jsx(SelectItem, { value: r.id, children: r.isAvailable ? r.name : `${r.name} ${t('submit.radiologistOffDuty')}` }, r.id)))] })] })] })), !isAmendment && !groupsLoading && groups.length === 0 && (_jsx("div", { className: "rounded-md border border-amber-500/30 bg-amber-500/10 p-3", children: _jsxs("div", { className: "flex items-start gap-2", children: [_jsx(AlertTriangle, { className: "mt-0.5 h-4 w-4 shrink-0 text-amber-400" }), _jsxs("div", { className: "space-y-1.5 text-sm", children: [_jsx("p", { className: "font-medium text-amber-200", children: t('submit.noGroupTitle') }), canManageGroups ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-muted-foreground", children: t('submit.noGroupAdmin') }), _jsx("button", { type: "button", onClick: onConnectGroups, className: "text-primary text-xs font-medium underline underline-offset-2", children: t('submit.noGroupLink') })] })) : (_jsx("p", { className: "text-muted-foreground", children: t('submit.noGroupContactAdmin') }))] })] }) })), !isAmendment && (_jsxs("div", { children: [_jsx(Label, { className: "text-muted-foreground text-xs", children: t('submit.priorityLabel') }), _jsxs(Select, { value: priority, onValueChange: (v) => setPriority(v), children: [_jsx(SelectTrigger, { className: "mt-1 h-9 w-48 text-sm", children: _jsx(SelectValue, {}) }), _jsx(SelectContent, { children: offeredPriorities.map((p) => (_jsx(SelectItem, { value: p.key, children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: `h-2 w-2 rounded-full ${PRIORITY_DOT_CLASS[p.key] ?? 'bg-gray-400'}` }), _jsx("span", { children: p.label ?? (TIER_LABEL_KEY[p.key] ? t(TIER_LABEL_KEY[p.key]) : p.key) }), _jsx("span", { className: "text-muted-foreground ml-1 text-xs", children: t('submit.tatLabel', {
                                                    tat: formatTat(p.tatMinutes, t('submit.unitMin'), t('submit.unitHr')),
                                                }) })] }) }, p.key))) })] })] })), _jsx(RequestFieldsForm, { fields: requestFields, values: requestValues, onChange: onRequestValueChange, errors: requestErrors, disabled: isAmendment, macros: macros }), _jsxs("div", { children: [_jsxs(Label, { className: "text-muted-foreground text-xs", children: [t('submit.notifyEmail'), _jsx("span", { className: "text-muted-foreground/50 ml-1", children: t('submit.optional') })] }), _jsx("p", { className: "text-muted-foreground mb-2 text-[10px]", children: t('submit.notifyEmailHelp') }), _jsx(EmailChipsInput, { emails: notifyEmails, onChange: setNotifyEmails, placeholder: t('submit.emailPlaceholder'), addLabel: t('submit.emailAdd'), removeLabel: (email) => t('submit.emailRemove', { email }) })] }), _jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs(Label, { htmlFor: "internalNotes", className: "text-muted-foreground text-xs", children: [t('submit.internalNotes'), _jsx("span", { className: "text-muted-foreground/50 ml-1", children: t('submit.internalNotesHint') })] }), notesMacrosHasMacros && (_jsx(MacroPopover, { macros: macros, onInsert: notesMacrosInsert, ...notesMacrosPicker }))] }), _jsx("textarea", { id: "internalNotes", ref: notesMacrosRef, value: internalNotes, onChange: (e) => setInternalNotes(e.target.value), onKeyDown: notesMacrosKeyDown, placeholder: t('submit.internalNotesPlaceholder'), className: "border-border bg-background placeholder:text-muted-foreground focus:ring-primary mt-1 min-h-[50px] w-full resize-y rounded-md border p-3 text-sm focus:ring-1 focus:outline-none" })] })] }));
}
// ═══════════════════════════════════════════
// Related Studies / Visit bundling (right column)
// ═══════════════════════════════════════════
function RelatedStudiesSection({ candidates, includedIds, onAdd, onRemove, }) {
    const { t } = useTranslation('teleconsultation');
    return (_jsxs("div", { className: "border-border mt-5 border-t pt-5", children: [_jsxs("h3", { className: "text-muted-foreground mb-1 flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase", children: [_jsx(Layers, { className: "h-3.5 w-3.5" }), t('submit.relatedStudiesTitle'), _jsx("span", { className: "text-muted-foreground/50 normal-case", children: t('submit.optional') })] }), _jsx("p", { className: "text-muted-foreground mb-3 text-[10px]", children: t('submit.relatedStudiesHelp') }), _jsx(BundleStudyPicker, { candidates: candidates, includedIds: includedIds, onAdd: onAdd, onRemove: onRemove, emptyHint: t('bundle.noCandidates') })] }));
}
// ═══════════════════════════════════════════
// Amendment Banner
// ═══════════════════════════════════════════
function AmendmentBanner({ existingCase }) {
    const { t } = useTranslation('teleconsultation');
    return (_jsx("div", { className: "mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3", children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx(AlertTriangle, { className: "mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" }), _jsxs("div", { children: [_jsx("p", { className: "text-sm font-medium text-amber-400", children: t('submit.amendmentBannerTitle') }), _jsxs("p", { className: "text-muted-foreground mt-0.5 text-xs", children: [t('submit.amendmentBannerSubmitted', {
                                    date: formatDateTime(existingCase.submittedAt, NUMERIC_DATE_TIME),
                                }), ' ', "\u00B7 ", t('submit.amendmentBannerStatus', { status: existingCase.status }), existingCase.claimedByName
                                    ? t('submit.amendmentBannerAssigned', { name: existingCase.claimedByName })
                                    : t('submit.amendmentBannerUnassigned')] }), _jsx("p", { className: "text-muted-foreground mt-1 text-xs", children: t('submit.amendmentBannerHelp') })] })] }) }));
}
// ═══════════════════════════════════════════
// AI Review Dialog
// ═══════════════════════════════════════════
function AIReviewDialog({ result, onClose, onSubmitAnyway, }) {
    const { t } = useTranslation('teleconsultation');
    const hasErrors = result.issues.some((i) => i.severity === 'error');
    const allClear = result.issues.length === 0;
    return (_jsx("div", { className: "absolute inset-0 z-50 flex items-center justify-center bg-black/60", children: _jsxs("div", { className: "border-border bg-panel-chrome mx-6 w-full max-w-lg rounded-xl border shadow-2xl", children: [_jsxs("div", { className: "border-border flex items-center gap-3 border-b px-5 py-4", children: [allClear ? (_jsx(CheckCircle2, { className: "h-5 w-5 text-emerald-400" })) : hasErrors ? (_jsx(AlertTriangle, { className: "h-5 w-5 text-red-400" })) : (_jsx(AlertTriangle, { className: "h-5 w-5 text-amber-400" })), _jsxs("div", { children: [_jsx("p", { className: "text-sm font-semibold", children: allClear
                                        ? t('submit.reviewLooksGood')
                                        : hasErrors
                                            ? t('submit.reviewIssuesFound')
                                            : t('submit.reviewWarnings') }), _jsx("p", { className: "text-muted-foreground text-xs", children: result.summary })] })] }), result.issues.length > 0 && (_jsx("div", { className: "max-h-[300px] overflow-y-auto px-5 py-3", children: _jsx("div", { className: "space-y-2.5", children: result.issues.map((issue) => (_jsxs("div", { className: cn('rounded-lg border px-4 py-3', issue.severity === 'error'
                                ? 'border-red-500/30 bg-red-500/5'
                                : 'border-amber-500/30 bg-amber-500/5'), children: [_jsx("p", { className: cn('text-sm font-medium', issue.severity === 'error' ? 'text-red-400' : 'text-amber-400'), children: issue.title }), _jsx("p", { className: "text-muted-foreground mt-0.5 text-xs", children: issue.detail })] }, `${issue.severity}-${issue.title}`))) }) })), _jsxs("div", { className: "border-border flex items-center justify-end gap-3 border-t px-5 py-3", children: [_jsx(Button, { variant: "outline", size: "sm", onClick: onClose, children: allClear ? t('submit.reviewClose') : t('submit.reviewFixIssues') }), !allClear && (_jsxs(Button, { variant: hasErrors ? 'destructive' : 'default', size: "sm", onClick: onSubmitAnyway, children: [_jsx(Send, { className: "mr-1.5 h-3 w-3" }), t('submit.reviewSubmitAnyway')] })), allClear && (_jsxs(Button, { size: "sm", onClick: onSubmitAnyway, children: [_jsx(Send, { className: "mr-1.5 h-3 w-3" }), t('submit.reviewSubmitRequest')] }))] })] }) }));
}
